import type { ChangeEvent } from "react";
import type { FieldDef } from "@eaub/shared";
import { asBoolean, asNumberInput, asString } from "../../lib/values";
import { isoToLocalInput, localInputToIso } from "../../lib/format";
import { MediaInput } from "./MediaInput";
import { ReferenceInput } from "./ReferenceInput";

interface FieldInputProps {
  field: FieldDef;
  value: unknown;
  onChange: (value: unknown) => void;
}

/** Renders the appropriate input control for a single content-type field. */
export function FieldInput({ field, value, onChange }: FieldInputProps) {
  const inputId = `field-${field.key}`;

  switch (field.kind) {
    case "text":
      return (
        <input
          id={inputId}
          type="text"
          className="input"
          value={asString(value)}
          maxLength={field.config.maxLength}
          onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
        />
      );

    case "richtext":
      return (
        <textarea
          id={inputId}
          className="input textarea"
          rows={8}
          value={asString(value)}
          maxLength={field.config.maxLength}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value)}
        />
      );

    case "number":
      return (
        <input
          id={inputId}
          type="number"
          className="input"
          value={asNumberInput(value)}
          min={field.config.min}
          max={field.config.max}
          step={field.config.integer ? 1 : "any"}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            const raw = e.target.value;
            onChange(raw === "" ? undefined : Number(raw));
          }}
        />
      );

    case "boolean":
      return (
        <label className="checkbox-row">
          <input
            id={inputId}
            type="checkbox"
            checked={asBoolean(value)}
            onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.checked)}
          />
          <span>{asBoolean(value) ? "Yes" : "No"}</span>
        </label>
      );

    case "date":
      return (
        <input
          id={inputId}
          type="datetime-local"
          className="input"
          value={isoToLocalInput(value)}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            onChange(localInputToIso(e.target.value) ?? undefined)
          }
        />
      );

    case "media":
      return (
        <MediaInput
          id={inputId}
          multiple={field.config.multiple ?? false}
          value={value}
          onChange={onChange}
        />
      );

    case "reference":
      if (!field.config.refTypeId) {
        return <p className="muted">This reference field has no target type configured.</p>;
      }
      return (
        <ReferenceInput
          id={inputId}
          refTypeId={field.config.refTypeId}
          multiple={field.config.multiple ?? false}
          value={value}
          onChange={onChange}
        />
      );

    default:
      return null;
  }
}
